import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Boxes, Download, FileDown } from "lucide-react";
import { toast } from "../../hooks/use-toast";

function toCsv(vouchers) {
  const rows = [["Code", "Title", "Value", "Balance", "Status", "Expires"]];
  vouchers.forEach((v) => rows.push([
    v.code, v.product_title, v.original_amount, v.balance, v.status, String(v.expires_at || "").slice(0, 10),
  ]));
  return rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function BulkTab() {
  const [products, setProducts] = useState([]);
  const [mode, setMode] = useState("product"); // product | custom
  const [form, setForm] = useState({ product_id: "", custom_title: "", custom_amount: "", quantity: 10, recipient_label: "", recipient_name: "", recipient_email: "", send_email: false });
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);

  useEffect(() => {
    api.get("/business/products").then(({ data }) => {
      setProducts(data);
      if (data[0]) setForm((f) => ({ ...f, product_id: data[0].id }));
    });
  }, []);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true); setBatch(null);
    const payload = {
      quantity: parseInt(form.quantity) || 1, recipient_label: form.recipient_label, origin: window.location.origin,
      send_email: form.send_email, recipient_email: form.recipient_email, recipient_name: form.recipient_name,
    };
    if (mode === "product") payload.product_id = form.product_id;
    else { payload.custom_title = form.custom_title; payload.custom_amount = parseFloat(form.custom_amount) || 0; }
    try {
      const { data } = await api.post("/business/bulk-issue", payload);
      setBatch(data.vouchers);
      const emailNote = data.email_attempted ? ` — ${data.emailed} emailed to ${form.recipient_email}` : "";
      toast({ title: "Batch created", description: `${data.count} vouchers generated${emailNote}.` });
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Failed", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form onSubmit={generate} className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-[#eefafa] flex items-center justify-center"><Boxes className="text-[#2ec9c9]" size={22} /></div>
          <div>
            <h3 className="font-semibold text-[#16243b]">Issue corporate vouchers</h3>
            <p className="text-sm text-[#8a97a8]">Generate a batch (no payment) and export as CSV.</p>
          </div>
        </div>

        <div className="flex gap-2">
          {[["product", "From a product"], ["custom", "Custom amount"]].map(([m, l]) => (
            <button type="button" key={m} onClick={() => setMode(m)}
              className={`flex-1 rounded-xl py-2.5 text-sm font-medium border ${mode === m ? "bg-[#16243b] text-white border-[#16243b]" : "border-gray-200 text-[#5a6b80]"}`}>
              {l}
            </button>
          ))}
        </div>

        {mode === "product" ? (
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Product</label>
            <select value={form.product_id} onChange={set("product_id")} required
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
              {products.length === 0 && <option value="">No products — add one first</option>}
              {products.map((p) => <option key={p.id} value={p.id}>{p.title} — £{p.price.toFixed(2)}</option>)}
            </select>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">Title</label>
              <input value={form.custom_title} onChange={set("custom_title")} placeholder="Corporate £100" required
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">Amount (£)</label>
              <input type="number" step="0.01" value={form.custom_amount} onChange={set("custom_amount")} required
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Quantity</label>
            <input type="number" min="1" max="500" value={form.quantity} onChange={set("quantity")} required
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Label (optional)</label>
            <input value={form.recipient_label} onChange={set("recipient_label")} placeholder="Acme Corp"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
          </div>
        </div>

        <div className="rounded-xl border border-gray-100 p-4 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="bulk-email-toggle">
            <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
              className="w-4 h-4 accent-[#2ec9c9]" />
            <span className="text-sm font-medium text-[#16243b]">Email these vouchers to a customer</span>
          </label>
          {form.send_email && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Customer name</label>
                <input value={form.recipient_name} onChange={set("recipient_name")} placeholder="Jane Smith" data-testid="bulk-recipient-name"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Customer email</label>
                <input type="email" required={form.send_email} value={form.recipient_email} onChange={set("recipient_email")} placeholder="jane@email.com" data-testid="bulk-recipient-email"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <p className="col-span-2 text-xs text-[#8a97a8]">Each generated voucher (PDF + code) will be emailed to this address. Requires SMTP to be configured by the platform.</p>
            </div>
          )}
        </div>

        <button disabled={loading} className="gp-btn-teal w-full rounded-xl py-3 font-medium disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <Boxes size={18} />}
          {loading ? "Generating…" : "Generate batch"}
        </button>
      </form>

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-[#16243b]">{batch ? `${batch.length} codes generated` : "Generated codes appear here"}</p>
          {batch && (
            <button onClick={() => download("voucher-batch.csv", toCsv(batch))} className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2">
              <Download size={16} /> Export CSV
            </button>
          )}
        </div>
        <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-h-[420px] overflow-y-auto">
          {batch ? (
            <table className="w-full text-sm">
              <thead className="bg-[#f6f8fb] text-[#8a97a8] sticky top-0"><tr>{["Code", "Value", "Expires"].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {batch.map((v) => (
                  <tr key={v.id} className="border-t border-gray-50">
                    <td className="px-5 py-2.5 font-mono font-semibold text-[#16243b]">{v.code}</td>
                    <td className="px-5 py-2.5 text-[#5a6b80]">£{v.original_amount.toFixed(2)}</td>
                    <td className="px-5 py-2.5 text-[#5a6b80]">{String(v.expires_at).slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center text-[#8a97a8] flex flex-col items-center gap-2">
              <FileDown size={28} /> Generate a batch to see codes and export.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
